import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Briefcase, User } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "./AboutPage";
import { Reveal } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";

export default function ContactPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", looking: "hiring", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", {
        company_name: form.name,
        entity_type: "individual",
        contact_person: form.name,
        phone: form.phone,
        email: form.email,
        categories: [],
        notes: `[${t(`contact.${form.looking}`)}] ${form.message}`,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", phone: "", email: "", looking: "hiring", message: "" });
    } catch {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: MapPin, title: t("contact.location"), body: t("footer.office"), testid: "contact-location" },
    { icon: Phone, title: t("contact.call_email"), body: "+91 98672 88688 • hire@hirehost.in", testid: "contact-phone" },
    { icon: Clock, title: t("contact.hours_t"), body: t("contact.hours"), testid: "contact-hours" },
  ];

  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="contact-page">
      <Header />
      <PageHero overline={t("contact.overline")} title={t("contact.title")} sub={t("contact.sub")} testid="contact-title" img="/assets/hero-3.jpg" />

      <section className="pb-14 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-5">
          {cards.map((c) => (
            <Reveal key={c.testid}>
              <div className="card-light p-6 text-center h-full" data-testid={c.testid}>
                <div className="w-12 h-12 mx-auto rounded-xl bg-[#E8842C] flex items-center justify-center mb-4">
                  <c.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-head font-bold text-[#1B2A4E] mb-2">{c.title}</h3>
                <p className="text-sm text-[#1B2A4E]/60 leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-28 px-6 sm:px-12">
        <Reveal>
          <form onSubmit={submit} className="max-w-3xl mx-auto rounded-3xl border border-[#1B2A4E]/10 bg-white p-7 sm:p-10 shadow-sm space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="label">{t("apply.name")} *</label>
                <input data-testid="contact-name" required className="field" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("apply.phone")} *</label>
                <input data-testid="contact-phone-input" required className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("apply.email")}</label>
                <input data-testid="contact-email" type="email" className="field" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <label className="label">{t("contact.looking")}</label>
                <div className="flex gap-2" data-testid="contact-looking-group">
                  {[{ v: "hiring", icon: Briefcase }, { v: "job", icon: User }].map(({ v, icon: Icon }) => (
                    <button
                      type="button"
                      key={v}
                      data-testid={`contact-looking-${v}`}
                      onClick={() => set("looking", v)}
                      className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${
                        form.looking === v ? "bg-[#1B2A4E] border-[#1B2A4E] text-white" : "border-[#1B2A4E]/20 text-[#1B2A4E]/60 hover:border-[#E8842C]"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {t(`contact.${v}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="label">{t("contact.message")}</label>
                <textarea data-testid="contact-message" rows={4} className="field" value={form.message} onChange={(e) => set("message", e.target.value)} />
              </div>
            </div>
            <button type="submit" data-testid="contact-submit-btn" disabled={sending} className="btn-primary w-full justify-center !py-4 disabled:opacity-50">
              {t("contact.send")} <Send className="w-4 h-4" />
            </button>
          </form>
        </Reveal>
      </section>
      <Footer />
    </div>
  );
}
